import { useEmployee } from '@omnipresentgroup/domains';
import { getCurrencyInputStep } from '@omnipresentgroup/helpers';
import type { JSONSchema7 } from 'json-schema';
import { useEffect, useState } from 'react';

type Props = {
  employeeId: string;
};

export const useSalaryChangeSchema = ({ employeeId }: Props) => {
  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState({});

  const { data: employee, isLoading } = useEmployee({
    id: employeeId,
    enabled: Boolean(employeeId),
  });

  const currency = employee?.employmentDetails?.annualBaseSalary.currency;
  const currentSalary = employee?.employmentDetails?.annualBaseSalary.amount;
  const hoursOfWork = employee?.employmentDetails?.hoursOfWork;
  const jobTitle = employee?.employmentDetails?.jobTitle;
  const jobDescription = employee?.employmentDetails?.jobDescription;

  useEffect(() => {
    setSchema({
      title: '',
      type: 'object',
      required: [
        'reason',
        'newAnnualGrossSalary',
        'effectiveDate',
        'weeklyWorkingHours',
        'jobTitle',
        'jobDescription',
      ],
      properties: {
        reason: {
          type: 'string',
          title: 'Reason',
          oneOf: [
            {
              const: 'Promotion',
              title: 'Promotion',
            },
            {
              const: 'Annual raise',
              title: 'Annual raise',
            },
            {
              const: 'Other reason',
              title: 'Other reason',
            },
          ],
        },
        currentAnnualGrossSalary: {
          type: 'object',
          title: 'Current annual gross salary',
          required: ['amount', 'currency'],
          properties: {
            currency: {
              type: 'string',
              title: 'Current annual gross salary currency',
              readOnly: true,
              default: currency,
            },
            amount: {
              type: 'number',
              title: 'Current annual gross salary amount',
              readOnly: true,
              default: currentSalary,
            },
          },
        },
        newAnnualGrossSalary: {
          type: 'object',
          title: 'New annual gross salary',
          required: ['amount', 'currency'],
          properties: {
            currency: {
              type: 'string',
              title: 'New annual gross salary currency',
              readOnly: true,
              default: currency,
            },
            amount: {
              type: 'number',
              title: 'New annual gross salary amount',
              minimum: 0.01,
            },
          },
        },
        effectiveDate: {
          type: 'string',
          title: 'Effective date',
          description:
            'Effective date must fall between the employment start date and end date',
          format: 'date',
        },
        weeklyWorkingHours: {
          type: 'number',
          title: 'Weekly working hours',
          default: hoursOfWork,
        },
        jobTitle: {
          type: 'string',
          title: 'Job title',
          default: jobTitle,
        },
        jobDescription: {
          type: 'string',
          title: 'Job description',
          default: jobDescription,
        },
        additionalNotes: {
          type: 'string',
          title: 'Additional notes',
        },
        enabledNotifications: {
          type: 'boolean',
          title: 'Enable notifications',
        },
      },
    });

    setUiSchema({
      'ui:submitButtonOptions': {
        norender: true,
      },
      'ui:order': [
        'reason',
        'currentAnnualGrossSalary',
        'newAnnualGrossSalary',
        'effectiveDate',
        'weeklyWorkingHours',
        'jobTitle',
        'jobDescription',
        'additionalNotes',
        'enabledNotifications',
      ],
      reason: {
        'ui:widget': 'radio',
        'ui:placeholder': 'Select a reason',
        'ui:autofocus': true,
      },
      currentAnnualGrossSalary: {
        'ui:columns': { columns: 6 },
        'ui:order': ['currency', 'amount'],
        amount: {
          'ui:label': false,
          'ui:placeholder': 'Enter an amount',
          'ui:span': 4,
        },
        currency: {
          'ui:label': false,
          'ui:placeholder': 'Select',
          'ui:span': 2,
        },
      },
      newAnnualGrossSalary: {
        'ui:columns': { columns: 6 },
        'ui:order': ['currency', 'amount'],
        amount: {
          'ui:label': false,
          'ui:placeholder': 'Enter an amount',
          'ui:span': 4,
          'ui:options': {
            step: getCurrencyInputStep(currency),
          },
        },
        currency: {
          'ui:label': false,
          'ui:placeholder': 'Select',
          'ui:span': 2,
        },
      },
      effectiveDate: {
        'ui:placeholder': 'mmm dd, yyyy',
        'ui:options': {
          dateFormat: 'MMM dd, yyyy',
        },
      },
      jobTitle: {
        'ui:placeholder': `Enter the employee's job title`,
      },
      jobDescription: {
        'ui:widget': 'textarea',
        'ui:options': {
          rows: 1,
        },
      },
      additionalNotes: {
        'ui:widget': 'textarea',
        'ui:options': {
          rows: 1,
        },
      },
      weeklyWorkingHours: {
        'ui:placeholder': 'Enter number of working hours per week',
      },
      enabledNotifications: {
        // 'ui:widget': isCSAdmin ? 'checkbox' : 'hidden',
        'ui:widget': 'hidden', //TODO: show this for cs admins
      },
    });
  }, [currency, currentSalary, hoursOfWork, jobDescription, jobTitle]);

  return {
    isLoading: Boolean(employeeId) && isLoading,
    schema,
    uiSchema,
  };
};
